import {ActionList, Box, Radio, type ActionListItemProps, merge} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {testIdProps} from '@github-ui/test-id-props'
import {type SafeHTMLString, SafeHTMLText} from '@github-ui/safe-html'
import {LoadingSkeleton} from '@github-ui/skeleton/LoadingSkeleton'

type ActionListItemLabelCommonProps = {
  description?: string
  hexColor: string
  id: string
  name: string
  nameHTML?: SafeHTMLString
  selected?: boolean
} & ActionListItemProps
type ActionListItemLabelSelectTypeProps =
  | {
      selectType: 'single'
      /*
       * radioGroupName: The name used to identify the group of radios this Action List Item is
       * associate with.This is required for single select type.
       * For more details on Radio Group: https://primer.style/components/radio-group/react/alpha
       */
      radioGroupName: string
    }
  | {
      selectType: 'multiple'
      radioGroupName?: never
    }
  | {
      selectType: 'instant'
      radioGroupName?: never
    }
  | {
      selectType: 'action'
      radioGroupName?: never
    }

export type ActionListItemLabelProps = ActionListItemLabelCommonProps & ActionListItemLabelSelectTypeProps

export function ActionListItemLabel({
  description,
  hexColor,
  id,
  name,
  nameHTML,
  selectType,
  selected,
  radioGroupName,
  sx: externalSx,
  ...props
}: ActionListItemLabelProps) {
  const isNativeActionListItemSelection = selectType === 'multiple' || selectType === 'instant'
  const hasCustomLeadingVisual = selectType === 'single'

  const defaultStyles = description
    ? {}
    : // Remove the bottom margin to center name when there is no description
      {
        'div[data-component="ActionList.Item--DividerContainer"] > span:nth-of-type(1)': {
          mb: 0,
        },
      }
  const styles = externalSx ? merge<BetterSystemStyleObject>(defaultStyles, externalSx) : defaultStyles

  return (
    <ActionList.Item key={id} {...props} {...(isNativeActionListItemSelection ? {selected} : {})} sx={styles}>
      {hasCustomLeadingVisual ? (
        <ActionList.LeadingVisual sx={{maxWidth: '38px'}}>
          {selectType === 'single' && (
            <Radio name={radioGroupName} value={id} checked={selected} sx={{mr: 2}} {...testIdProps('label-radio')} />
          )}
          <Circle color={hexColor} />
        </ActionList.LeadingVisual>
      ) : (
        <ActionList.LeadingVisual>
          <Circle color={hexColor} />
        </ActionList.LeadingVisual>
      )}
      {(nameHTML && <SafeHTMLText sx={{wordBreak: 'break-word'}} html={nameHTML} />) || name}
      <ActionList.Description variant="block">{description}</ActionList.Description>
    </ActionList.Item>
  )
}

export function ActionListItemLabelSkeleton({
  selectType,
  sx: externalSx,
  ...props
}: {
  selectType: 'single' | 'multiple' | 'instant' | 'action'
  sx?: BetterSystemStyleObject
}) {
  const defaultStyles = {display: 'flex', gap: 2}
  const styles = externalSx ? merge<BetterSystemStyleObject>(defaultStyles, externalSx) : defaultStyles

  return (
    <Box sx={styles} {...props}>
      {/* The width needs to be larger than the height to make a square */}
      {selectType !== 'action' && <LoadingSkeleton variant="rounded" height="sm" width="md" />}
      <Box sx={{height: '16px', alignItems: 'center', display: 'flex'}}>
        <LoadingSkeleton variant="elliptical" height="13px" width="13px" />
      </Box>
      <Box sx={{width: '100%'}}>
        <LoadingSkeleton sx={{width: '100%', mb: 2}} variant="rounded" height="sm" />
        <LoadingSkeleton sx={{width: '100%'}} variant="rounded" height="sm" />
      </Box>
    </Box>
  )
}

function Circle({color}: {color: string}) {
  return (
    <Box
      sx={{
        width: 12,
        height: 12,
        borderRadius: '100%',
      }}
      style={{
        backgroundColor: `#${color}`,
      }}
      {...testIdProps('label-circle')}
    />
  )
}

try{ ActionListItemLabel.displayName ||= 'ActionListItemLabel' } catch {}
try{ ActionListItemLabelSkeleton.displayName ||= 'ActionListItemLabelSkeleton' } catch {}
try{ Circle.displayName ||= 'Circle' } catch {}